import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useNotifications, useMarkNotificationsSeen } from './useNotifications';
import { COLUMN_BY_STATUS } from '@/features/board/constants';
import { Skeleton, SkeletonGroup } from '@/components/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import type { Notification } from '@/types/api';

const VERB: Record<string, string> = {
  created: 'assigned you',
  note: 'added a note to',
  status_changed: 'moved',
  reassigned: 'reassigned',
  priority_changed: 'changed the priority of',
  due_date_changed: 'changed the reminder on',
  tag_changed: 'retagged',
  archived: 'archived',
  restored: 'restored',
};

function actorName(item: Notification): string {
  if (item.actorType === 'agent') return item.actorDeskId ? `The ${item.actorDeskId} desk` : 'The agent';
  if (item.actorType === 'admin') return item.actorName ?? 'An admin';
  if (item.actorType === 'system') return 'The system';
  return item.actorName ?? 'Someone';
}

/** Relative, because "2h ago" is the only thing anyone reads a feed timestamp for. */
function ago(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * `variant` is where it lives, and that decides which way the panel opens.
 *
 * In the sidebar footer it must open UPWARD — anchored below, it renders off the
 * bottom of the viewport. In the app header the opposite is true. Same component,
 * because two copies of a notification list is how one of them quietly stops matching.
 */
export function NotificationBell({
  collapsed = false,
  variant = 'sidebar',
}: {
  collapsed?: boolean;
  variant?: 'sidebar' | 'header';
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const notifications = useNotifications();
  const markSeen = useMarkNotificationsSeen();
  const ref = useRef<HTMLDivElement>(null);

  const unseen = notifications.data?.unseenCount ?? 0;

  // Opening the panel IS reading it, so the badge clears on open rather than on some
  // separate dismiss the user would have to find.
  const { mutate: mark } = markSeen;
  useEffect(() => {
    if (open && unseen > 0) mark();
  }, [open, unseen, mark]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    // pointerdown, not click: a click listener fires after the target's own handler,
    // so clicking a row would close the panel and then navigate, in that order.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={unseen > 0 ? `Notifications, ${unseen} unread` : 'Notifications'}
        aria-expanded={open}
        title="Notifications"
        className={`flex items-center gap-2.5 rounded-lg text-sm transition-colors hover:opacity-80 ${
          variant === 'header'
            ? 'size-10 justify-center'
            : collapsed
              ? 'size-10 justify-center px-0 py-2'
              : 'w-full px-2 py-2'
        }`}
        style={{ color: open ? 'var(--accent)' : 'var(--text-muted)' }}
      >
        <span className="relative flex shrink-0">
          <Bell size={17} />
          {unseen > 0 && (
            <span
              className="absolute -right-1.5 -top-1.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-semibold tabular-nums"
              style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
            >
              {unseen > 99 ? '99+' : unseen}
            </span>
          )}
        </span>
        {variant === 'sidebar' && !collapsed && (
          <span className="flex-1 text-left">Notifications</span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className={`absolute z-50 flex max-h-[70vh] w-[min(22rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-xl border ${
            variant === 'header' ? 'right-0 top-full mt-2' : 'bottom-full left-0 mb-2'
          }`}
          style={{
            borderColor: 'var(--border)',
            background: 'var(--surface)',
            boxShadow: 'var(--shadow-panel)',
          }}
        >
          <header
            className="flex shrink-0 items-center justify-between border-b px-3 py-2.5"
            style={{ borderColor: 'var(--border)' }}
          >
            <h2 className="text-xs font-semibold">Notifications</h2>
            <span className="text-[11px]" style={{ color: 'var(--text-faint)' }}>
              your cards only
            </span>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar">
            {notifications.isPending && (
              <SkeletonGroup label="Loading notifications" className="flex flex-col gap-3 p-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col gap-1.5">
                    <Skeleton width={i % 2 ? '80%' : '60%'} height={11} />
                    <Skeleton width={90} height={9} />
                  </div>
                ))}
              </SkeletonGroup>
            )}

            {notifications.isError && (
              <p className="p-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                Could not load notifications.
              </p>
            )}

            {notifications.data?.items.length === 0 && (
              <EmptyState
                size="sm"
                title="Nothing yet."
                description="When a desk assigns you a card or adds to one, it shows up here."
              />
            )}

            <ol>
              {notifications.data?.items.map((item) => {
                const column = COLUMN_BY_STATUS.get(item.taskStatus);

                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        // ?task= rather than local state: the board is a different
                        // subtree, and this also makes a card linkable into Telegram.
                        void navigate(`/board?task=${item.taskId}`);
                        setOpen(false);
                      }}
                      className="flex w-full items-start gap-2.5 border-b px-3 py-2.5 text-left transition-colors hover:opacity-80"
                      style={{
                        borderColor: 'var(--border)',
                        background: item.unseen ? 'var(--accent-soft)' : 'transparent',
                      }}
                    >
                      {column && (
                        <column.icon
                          size={14}
                          className="mt-0.5 shrink-0"
                          style={{ color: column.colorVar }}
                        />
                      )}

                      <span className="min-w-0 flex-1">
                        <span className="block text-xs leading-snug">
                          <span className="font-medium">{actorName(item)}</span>{' '}
                          {VERB[item.eventType] ?? item.eventType}{' '}
                          <span className="font-mono text-[11px]">{item.reference}</span>
                          {item.fromStatus && item.toStatus && ` to ${item.toStatus}`}
                        </span>

                        <span className="mt-0.5 block truncate text-[11px]" style={{ color: 'var(--text-muted)' }}>
                          {item.body ?? item.taskTitle}
                        </span>

                        <span className="mt-0.5 block text-[10px]" style={{ color: 'var(--text-faint)' }}>
                          {ago(item.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            {notifications.data?.nextCursor && (
              <p className="py-3 text-center text-[11px]" style={{ color: 'var(--text-faint)' }}>
                Older notifications are on the cards themselves.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
